import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, FileSignature, Phone, MapPin, Calendar, Car, DollarSign, Eraser, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicContract() {
  const { contractId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [pollingDeposit, setPollingDeposit] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef({ down: false, last: null, hasInk: false });

  const loadContract = useCallback(() => {
    return fetch(`${API}/api/public/contracts/${contractId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => {
        setContract(d);
        if (d.client_name) setSignerName(d.client_name);
        if (d.status === 'signed') setSigned(true);
        setLoading(false);
        return d;
      })
      .catch(async (r) => {
        const e = r.json ? await r.json().catch(() => ({})) : {};
        setError(e.detail || 'Contract not found');
        setLoading(false);
      });
  }, [contractId]);

  useEffect(() => { loadContract(); }, [loadContract]);

  // After Stripe redirect: poll deposit status until paid
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const depositFlag = searchParams.get('deposit_paid');
    if (!depositFlag || !sessionId) return;
    let attempts = 0;
    let cancelled = false;
    setPollingDeposit(true);
    const poll = async () => {
      while (attempts < 8 && !cancelled) {
        attempts += 1;
        try {
          const r = await fetch(`${API}/api/public/contracts/${contractId}/deposit-status/${sessionId}`);
          const d = await r.json();
          if (d.payment_status === 'paid') {
            toast.success('Deposit received — your booking is locked in.');
            await loadContract();
            setPollingDeposit(false);
            // Clean URL
            setSearchParams({}, { replace: true });
            return;
          }
        } catch (e) { /* keep polling */ }
        await new Promise(res => setTimeout(res, 2000));
      }
      if (!cancelled) {
        setPollingDeposit(false);
        toast.info('Payment is still processing. Refresh in a moment.');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [searchParams, contractId, loadContract, setSearchParams]);

  const startDepositCheckout = async () => {
    setPayingDeposit(true);
    try {
      const r = await fetch(`${API}/api/public/contracts/${contractId}/deposit-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok || !d.checkout_url) throw new Error(d.detail || 'Could not start checkout');
      window.location.href = d.checkout_url;
    } catch (e) {
      toast.error(e.message);
      setPayingDeposit(false);
    }
  };

  // Sig pad helpers
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    // Set high-DPI canvas size
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
  }, [contract]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current.down = true; drawingRef.current.last = getPos(e); };
  const move = (e) => {
    if (!drawingRef.current.down) return;
    e.preventDefault();
    const c = canvasRef.current; const ctx = c.getContext('2d');
    const p = getPos(e);
    ctx.beginPath(); ctx.moveTo(drawingRef.current.last.x, drawingRef.current.last.y);
    ctx.lineTo(p.x, p.y); ctx.stroke();
    drawingRef.current.last = p;
    drawingRef.current.hasInk = true;
  };
  const end = () => { drawingRef.current.down = false; };
  const clearPad = () => {
    const c = canvasRef.current; const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    drawingRef.current.hasInk = false;
  };

  const submit = async () => {
    if (!signerName.trim()) { toast.error('Type your full name'); return; }
    if (!drawingRef.current.hasInk) { toast.error('Draw your signature'); return; }
    setSubmitting(true);
    try {
      const sig = canvasRef.current.toDataURL('image/png');
      const r = await fetch(`${API}/api/public/contracts/${contractId}/sign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: sig, signer_name: signerName.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not save signature');
      toast.success('Signed! ACE has been notified.');
      setSigned(true);
      setContract({ ...contract, status: 'signed', signer_name: signerName.trim(), signed_at: d.signed_at });
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  if (loading) return <CenterPanel><Loader2 className="h-7 w-7 animate-spin text-red-500" /></CenterPanel>;
  if (error) return (
    <CenterPanel>
      <AlertCircle className="h-12 w-12 text-red-500" />
      <h1 className="text-2xl font-bold mt-3">Contract Unavailable</h1>
      <p className="text-gray-500 mt-1">{error}</p>
      <p className="text-xs text-gray-400 mt-4">Reach us at (678) 846-3009 if you believe this is an error.</p>
    </CenterPanel>
  );

  const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-black text-white px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-black tracking-tight text-lg" data-testid="ace-home-link">ACE <span className="text-red-500">Auto Concierge</span></Link>
          <a href="tel:+16788463009" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"><Phone className="h-4 w-4" /> (678) 846-3009</a>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="public-contract-page">
        <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Service Agreement</p>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight mt-1" data-testid="contract-title">{contract.contract_type || 'ACE Service Agreement'}</h1>
        <p className="text-xs text-gray-500 mt-1">Contract <span className="font-mono">{contract.contract_id}</span></p>

        {signed && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3" data-testid="contract-signed-banner">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-green-900">Contract Signed</p>
              <p className="text-xs text-green-700 mt-0.5">
                Signed by <span className="font-semibold">{contract.signer_name || signerName}</span>
                {contract.signed_at && <> on {new Date(contract.signed_at).toLocaleString()}</>}
              </p>
            </div>
          </div>
        )}

        {contract.deposit_paid_at && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="contract-deposit-paid-banner">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-emerald-900">Deposit Paid {contract.deposit_paid_amount ? `· ${fmt$(contract.deposit_paid_amount)}` : ''}</p>
              <p className="text-xs text-emerald-700 mt-0.5">Your booking is locked in. A receipt was emailed to ACE.</p>
            </div>
          </div>
        )}

        {pollingDeposit && !contract.deposit_paid_at && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="deposit-polling-banner">
            <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
            <p className="text-sm text-amber-900">Confirming your payment with Stripe…</p>
          </div>
        )}

        {/* Details */}
        <div className="bg-white rounded-xl border border-gray-200 mt-5 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-2">
            <h2 className="font-bold text-lg">Reservation Details</h2>
            {contract.quote_number && <span className="text-[11px] font-mono text-gray-500" data-testid="contract-quote-number">{contract.quote_number}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 text-sm">
            <Detail label="Client" value={contract.client_name} />
            <Detail icon={Calendar} label="Event date" value={contract.event_date} />
            {contract.event_name && <Detail label="Event / Trip" value={contract.event_name} />}
            <Detail icon={Car} label="Vehicle" value={contract.vehicle} />
            <Detail label="Pickup time" value={contract.pickup_time} />
            <Detail label="Hours" value={contract.hours ? `${contract.hours} hours` : null} />
            <Detail icon={MapPin} label="Pickup" value={contract.pickup_address} />
            <Detail icon={MapPin} label="Drop-off" value={contract.dropoff_address} />
          </div>

          {/* Line items (Quote & Contract Builder) */}
          {Array.isArray(contract.line_items) && contract.line_items.length > 0 && (
            <div className="px-5 pb-4" data-testid="contract-line-items">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b-2 border-gray-900">
                  <th className="text-left py-1.5">Service</th><th className="text-right py-1.5">Hours</th><th className="text-right py-1.5">Amount</th>
                </tr></thead>
                <tbody>
                  {contract.line_items.map((li, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2">{li.desc || li.description || li.vehicle || li.name || 'Service'}{Number(li.discount) > 0 ? <span className="text-xs text-emerald-600 ml-1">(-{li.discount}%)</span> : null}</td>
                      <td className="py-2 text-right">{li.isFlat ? 'Flat' : (li.hours || '—')}</td>
                      <td className="py-2 text-right font-semibold">{fmt$(li.total ?? li.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Fee breakdown */}
          {contract.totals && (
            <div className="px-5 pb-4 text-sm" data-testid="contract-fee-breakdown">
              <div className="flex justify-between py-1 text-gray-600"><span>Subtotal</span><span>{fmt$(contract.totals.subtotal)}</span></div>
              {contract.totals.gratuity > 0 && <div className="flex justify-between py-1 text-gray-600"><span>Gratuity{contract.fees?.gratuityPct ? ` (${contract.fees.gratuityPct}%)` : ''}</span><span>{fmt$(contract.totals.gratuity)}</span></div>}
              {contract.totals.fuel > 0 && <div className="flex justify-between py-1 text-gray-600"><span>Fuel / travel surcharge</span><span>{fmt$(contract.totals.fuel)}</span></div>}
              {contract.totals.other > 0 && <div className="flex justify-between py-1 text-gray-600"><span>{contract.fees?.otherDesc || 'Other fees'}</span><span>{fmt$(contract.totals.other)}</span></div>}
              {contract.totals.tax > 0 && <div className="flex justify-between py-1 text-gray-600"><span>Tax{contract.fees?.taxPct ? ` (${contract.fees.taxPct}%)` : ''}</span><span>{fmt$(contract.totals.tax)}</span></div>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 p-5 bg-gray-50 border-t">
            <Detail icon={DollarSign} label="Total" value={contract.total_amount ? fmt$(contract.total_amount) : null} highlight />
            <Detail icon={DollarSign} label="Deposit required" value={contract.deposit_required ? fmt$(contract.deposit_required) : null} highlight />
          </div>
          {contract.notes && <p className="px-5 py-3 text-xs text-gray-500 border-t" data-testid="contract-notes"><b>Itinerary / Notes:</b> {contract.notes}</p>}
        </div>

        {/* Terms */}
        <div className="bg-white rounded-xl border border-gray-200 mt-5 p-5 text-sm leading-relaxed text-gray-700" data-testid="contract-terms">
          <h2 className="font-bold text-lg text-black mb-3">Terms & Conditions</h2>
          {(contract.contract_type || '').toLowerCase().includes('rental') ? (
            <ol className="list-decimal pl-5 space-y-2">
              <li><strong>Renter Eligibility:</strong> Renter must be 25+ years of age, present a valid driver's license, full-coverage insurance card naming ACE/the vehicle, and a major credit card matching the license.</li>
              <li><strong>Security Deposit:</strong> A refundable security deposit (separate from rental fees) is authorized at pickup and released within 7 business days after return, less any damage/cleaning/toll/fuel charges.</li>
              <li><strong>Mileage & Fuel:</strong> Rental includes a daily mileage allowance. Overage and fuel returned below pickup level are billed at posted rates. Premium fuel required.</li>
              <li><strong>Authorized Drivers:</strong> Only drivers listed and approved on this agreement may operate the vehicle. Unauthorized operation voids coverage and incurs a $1,000 fee.</li>
              <li><strong>Prohibited Use:</strong> No off-road use, racing, towing, commercial use, smoking inside the vehicle, transport of illegal substances, or operation while impaired.</li>
              <li><strong>Damage, Tolls & Citations:</strong> Renter is responsible for all damage (interior/exterior), tolls, parking, and citations during the rental period. Excessive cleaning starts at $250.</li>
              <li><strong>Geographic Limit:</strong> Vehicle must remain within the agreed geographic zone unless ACE provides written approval.</li>
              <li><strong>Cancellation:</strong> Cancellations within 48 hours of pickup forfeit 100% of the deposit. Reschedules subject to availability.</li>
              <li><strong>Return Condition:</strong> Vehicle must be returned in the condition provided, at the agreed time. Late returns are billed in 30-min increments.</li>
              <li><strong>Acceptance:</strong> By signing below, Renter acknowledges and accepts all terms above.</li>
            </ol>
          ) : (contract.contract_type || '').toLowerCase().includes('service') || (contract.contract_type || '').toLowerCase().includes('chauffeur') ? (
            <ol className="list-decimal pl-5 space-y-2">
              <li><strong>Service Window:</strong> ACE provides a professional chauffeur for the agreed hours. Overtime is billed in 30-min increments at the same hourly rate.</li>
              <li><strong>Payment:</strong> The deposit reserves the vehicle/chauffeur. Balance is due no later than 24 hours before the scheduled pickup.</li>
              <li><strong>Cancellation:</strong> Cancellations within 48 hours of the event forfeit the deposit. Reschedules accommodated subject to availability.</li>
              <li><strong>Conduct & Capacity:</strong> Vehicle occupancy must not exceed the manufacturer rating. Smoking, illegal substances, and excessive disorderly conduct are prohibited.</li>
              <li><strong>Damage & Cleaning:</strong> Cleaning fees (starting at $250) and damage repair are billed to the contracting party.</li>
              <li><strong>Gratuity:</strong> 20% chauffeur gratuity is not included unless explicitly listed in your invoice.</li>
              <li><strong>Substitution & Acts of God:</strong> ACE reserves the right to substitute a comparable vehicle for mechanical or safety reasons. ACE is not liable for delays caused by traffic, weather, or other forces beyond reasonable control.</li>
              <li><strong>Acceptance:</strong> By signing below, Client accepts these terms.</li>
            </ol>
          ) : (
            <ol className="list-decimal pl-5 space-y-2">
              <li>Client agrees to remit the balance no later than 24 hours prior to the event date and time.</li>
              <li>Cancellations within 48 hours of the event forfeit the deposit. Reschedules are accommodated subject to availability.</li>
              <li>Vehicle must be returned in the condition it was provided. Excessive cleaning, smoking, or damage will incur fees starting at $250.</li>
              <li>Chauffeur gratuity (20%) is not included unless explicitly listed in your invoice.</li>
              <li>ACE reserves the right to substitute a comparable vehicle if mechanical issues arise.</li>
              <li>By signing below, Client acknowledges and accepts these terms.</li>
            </ol>
          )}
          {contract.additional_terms && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Additional Terms</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{contract.additional_terms}</p>
            </div>
          )}
        </div>

        {/* Signature block */}
        {!signed && (
          <div className="bg-white rounded-xl border border-gray-200 mt-5 p-5" data-testid="signature-block">
            <h2 className="font-bold text-lg flex items-center gap-2"><FileSignature className="h-5 w-5 text-red-600" /> Sign to confirm</h2>
            <p className="text-xs text-gray-500 mt-1">Type your full legal name, then draw your signature in the box below.</p>

            <div className="mt-4">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Full legal name</label>
              <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Jane Doe" className="mt-1 h-11" data-testid="contract-signer-name" />
            </div>

            <div className="mt-4">
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Signature</label>
              <div className="mt-1 bg-white border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                <canvas
                  ref={canvasRef}
                  className="w-full touch-none"
                  style={{ height: 180, display: 'block' }}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  data-testid="signature-canvas"
                />
              </div>
              <button onClick={clearPad} className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1 mt-2" data-testid="clear-signature-btn"><Eraser className="h-3 w-3" /> Clear</button>
            </div>

            <Button onClick={submit} disabled={submitting} className="w-full h-12 mt-5 bg-red-600 hover:bg-red-700 text-base font-bold" data-testid="sign-contract-submit">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : 'Sign & Confirm Contract'}
            </Button>
            <p className="text-[11px] text-gray-400 mt-2 text-center">By tapping Sign, you agree to the terms above.</p>
          </div>
        )}

        {/* Deposit pay-now (visible after signing if deposit required & not yet paid) */}
        {signed && !contract.deposit_paid_at && Number(contract.deposit_required || 0) > 0 && (
          <div className="bg-black text-white rounded-xl mt-5 p-6" data-testid="deposit-pay-block">
            <div className="flex items-start gap-3">
              <CreditCard className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-bold text-lg">Lock in your booking</h2>
                <p className="text-sm text-gray-300 mt-1">Pay the {fmt$(contract.deposit_required)} deposit now to secure your reservation. Apple Pay, Google Pay, and all major cards accepted via Stripe.</p>
              </div>
            </div>
            <Button onClick={startDepositCheckout} disabled={payingDeposit} className="w-full h-12 mt-4 bg-red-600 hover:bg-red-700 text-base font-bold" data-testid="contract-pay-deposit-btn">
              {payingDeposit ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Opening secure checkout…</> : `Pay ${fmt$(contract.deposit_required)} Deposit Now`}
            </Button>
            <p className="text-[11px] text-gray-400 mt-2 text-center">Secured by Stripe · ACE receives an instant receipt</p>
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-6 text-center">
          ACE Auto Concierge · Atlanta, GA · (678) 846-3009 · YourAutoConcierge.com
        </p>
      </div>
    </div>
  );
}

const Detail = ({ icon: Icon, label, value, highlight }) => {
  if (!value) return null;
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <p className={`mt-1 ${highlight ? 'text-lg font-black text-black' : 'text-sm font-medium text-gray-800'}`}>{value}</p>
    </div>
  );
};

const CenterPanel = ({ children }) => (
  <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">{children}</div>
);
